import { useCallback, useState } from 'react';
import Button from '@/components/atoms/button';
import Input from '@/components/atoms/input';
import useDisclosure from '@/hooks/useDisclosure';
import { classnames } from '@/utils/classnames/classnames';
import { TagIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useController, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';
import Accordion from '../accordion';
import { DiscountFormProps } from './types';

const DiscountsForm = ({
  className,
  discounts,
  onSubmit,
  customError,
  onExpanded,
  onCollapsed,
  codeApplied,
  readOnly,
}: DiscountFormProps) => {
  const translate = useTranslations();

  const [codeRemovalProcessing, setCodeRemovalProcessing] = useState(false);

  // EWM-612 — no global mutation gate; the local isSubmitting / codeRemovalProcessing
  // flags own this form's in-flight states, and a cart write surfaces the processing
  // modal (which blocks interaction) rather than disabling these controls.
  const { isOpen: isExpanded, onOpen: onExpand, onClose: onCollapse } = useDisclosure();

  const [showSuccess, setShowSuccess] = useState(false);

  const {
    handleSubmit,
    setError,
    reset,
    control,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<{ code: string }>({
    defaultValues: { code: '' },
    mode: 'onChange',
  });

  const {
    // EWM-801 — `name` was already provided by react-hook-form and simply not
    // forwarded to the input, so the field rendered with neither id nor name
    // and Chrome flagged it on the cart page. The Input atom derives
    // `id={props.id || props.name}`, so passing name alone fixes both.
    field: { name, value, onChange, onBlur, ref },
  } = useController({
    name: 'code',
    control,
  });

  const discountsContainerClassName = classnames(
    'mt-4 flex flex-col items-stretch gap-3',
    discounts?.length === 0 ? 'pt-0' : 'pt-1',
  );

  const containerClassName = classnames('py-4 text-16 border-t border-gray-300 md:border-t-0', className);

  const onApplyDiscount = async (data: { code: string }) => {
    const code = data.code.trim();

    if (!code) return;

    const alreadyExists = discounts.find((discount) => discount.code === code);

    if (alreadyExists) {
      setError('code', { type: 'required', message: translate('cart.discount-coode-already-exists') });
      return;
    }

    const res = await onSubmit?.(code);

    if (!res?.success) {
      setError('code', {
        type: 'manual',
        message: customError || translate('cart.codeNotValid'),
      });
      return;
    }

    setShowSuccess(true);
    reset({ code: '' });

    if (typeof window !== 'undefined' && Array.isArray((window as unknown as { dataLayer?: unknown[] }).dataLayer)) {
      (window as unknown as { dataLayer: Record<string, unknown>[] }).dataLayer.push({
        event: 'discount_code_applied',
        discount_code: code,
      });
    }
  };

  const onClearForm = useCallback(() => {
    setShowSuccess(true);
    reset({ code: '' });
  }, [reset]);

  // EWM-760 — react-hook-form's `isSubmitSuccessful` flips to `true` whenever
  // a submission cycle completes without a thrown error or a field error set
  // via `setError` (this covers both the "code applied" success path AND a
  // submit of an already-empty code, which returns early with no error — a
  // pre-existing quirk this preserves as-is). Reading the FRESH value during
  // render and reacting to the true flip (rather than watching it in a
  // cascading `useEffect`) is the only safe conversion here: closures
  // captured in the `<form onSubmit>` handler would read a stale
  // `isSubmitSuccessful` from the render that scheduled the submit, not the
  // updated one — the render-time comparison below always sees the latest
  // value. Same trigger condition as the old
  // `useEffect(() => { if (isSubmitSuccessful) onClearForm(); }, [...])`,
  // one render sooner.
  // Calling `onClearForm()` (which invokes RHF's imperative `reset()`) here
  // means `reset()` runs DURING RENDER, not in an effect. That's the same
  // shape the auth hooks (`useNewPasswordForm`, `useRegistrationForm`) were
  // explicitly kept OUT of an adjust-during-render rewrite for — see
  // docs/superpowers/plans/ewm-760-disables.md — because RHF's internal
  // subscriber notifications can trip React's "Cannot update a component
  // while rendering a different component" warning when `reset()` updates a
  // CHILD component subscribed via `register`/`Controller`/`useFormState`.
  // It's safe here specifically because `useForm`/`useController` and the
  // `<Input>` they feed are all co-located in this single component — the
  // `<Input>` atom is a plain controlled component (no RHF hook of its own),
  // so there is no child-component subscriber for `reset()`'s notification
  // to land on mid-render. Verified empirically: `console.error` is
  // asserted not to fire across the success- and whitespace-submit specs
  // in discount-form.spec.tsx.
  const [prevIsSubmitSuccessful, setPrevIsSubmitSuccessful] = useState(isSubmitSuccessful);
  if (prevIsSubmitSuccessful !== isSubmitSuccessful) {
    setPrevIsSubmitSuccessful(isSubmitSuccessful);
    if (isSubmitSuccessful) onClearForm();
  }

  return (
    <div className={containerClassName}>
      <Accordion
        className="border-0"
        isExpanded={isExpanded}
        onExpand={() => {
          onExpand();
          onExpanded?.();
        }}
        onCollapse={() => {
          onCollapse();
          onCollapsed?.();
        }}
      >
        {/* EWM-480 — replace the +/− icons with the shared accordion's
            rotating chevron, inverted per the ticket: up when collapsed →
            down when expanded. */}
        <Accordion.Button
          defaultSpacing={false}
          withArrow
          invertChevron
          chevronTone="primary"
          className="text-primary"
          aria-label={translate('cart.discount-code-title')}
        >
          <span className="text-16 font-semibold">{translate('cart.discount-code-title')}</span>
        </Accordion.Button>

        <Accordion.Panel defaultSpacing={false}>
          <form className="flex flex-col items-stretch gap-3 pt-4" onSubmit={handleSubmit(onApplyDiscount)} noValidate>
            <Input
              ref={ref}
              name={name}
              value={value}
              onChange={(e) => {
                onChange(e);
                setShowSuccess(false);
              }}
              onBlur={onBlur}
              onFocus={() => {
                setShowSuccess(false);
              }}
              aria-label={translate('cart.discount-code')}
              className={classnames(
                errors.code ? 'border-red-500 text-red-500 focus:border-red-500' : 'border-neutral-300',
              )}
              placeholder={translate('cart.discount-enter')}
              disabled={isSubmitting}
              icon={
                errors.code ? (
                  <XMarkIcon
                    className="size-[20px] cursor-pointer"
                    onClick={() => {
                      reset({ code: '' });
                      setShowSuccess(false);
                    }}
                    data-testid="clear-input"
                  />
                ) : null
              }
              error={errors.code?.message}
              success={showSuccess ? codeApplied : undefined}
              valid={showSuccess}
              showValidIcon={false}
            />
            <Button type="submit" className="h-[42px] w-full" disabled={!value.trim() || isSubmitting || readOnly}>
              {translate('cart.apply-code')}
            </Button>
          </form>

          {discounts && discounts.length > 0 && (
            <div className={discountsContainerClassName}>
              {discounts.map((discount) => (
                <div
                  key={discount.code}
                  className={classnames(
                    'flex justify-between gap-2 rounded-sm border border-green-300 bg-green-100 px-3 py-2',
                    { 'opacity-50': codeRemovalProcessing },
                  )}
                >
                  <div className="flex items-center gap-2">
                    <TagIcon className="size-4 text-green-700" aria-hidden />
                    <span className="text-14 leading-[16px] font-semibold text-gray-700">{discount.code}</span>
                  </div>
                  <button
                    data-testid="remove-discount-code"
                    type="button"
                    disabled={codeRemovalProcessing}
                    className="text-14 shrink-0 font-semibold text-gray-700 underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={async () => {
                      setCodeRemovalProcessing(true);
                      const removed = await discount.onRemove?.()?.catch(() => false);
                      setCodeRemovalProcessing(false);

                      if (removed) {
                        if (
                          typeof window !== 'undefined' &&
                          Array.isArray((window as unknown as { dataLayer?: unknown[] }).dataLayer)
                        ) {
                          (window as unknown as { dataLayer: Record<string, unknown>[] }).dataLayer.push({
                            event: 'discount_code_removed',
                            discount_code: discount.code,
                          });
                        }
                      }
                    }}
                  >
                    {translate('common.remove')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Accordion.Panel>
      </Accordion>
    </div>
  );
};

export default DiscountsForm;
